/**
 * Client for assumption config change history endpoints.
 */

import { API_ENDPOINTS } from '../config/api';
import { authenticatedFetch } from '../utils/authenticatedFetch';
import type {
  ConfigHistoryDocument,
  ConfigHistoryKind,
} from '../types/configAssumptionsHistory';

function historyUrl(kind: ConfigHistoryKind): string {
  if (kind === 'fleet-cost') {
    return API_ENDPOINTS.fleetIntelligence.config.fleetCostAssumptionsHistory();
  }
  if (kind === 'profitability') {
    return API_ENDPOINTS.profitabilityV2.configHistory();
  }
  if (kind === 'vehicle-attributes') {
    return API_ENDPOINTS.fleetIntelligence.vehicles.attributesHistory();
  }
  return API_ENDPOINTS.utilisationV2.configHistory();
}

function historyClearUrl(kind: ConfigHistoryKind): string {
  if (kind === 'fleet-cost') {
    return API_ENDPOINTS.fleetIntelligence.config.fleetCostAssumptionsHistoryClear();
  }
  if (kind === 'profitability') {
    return API_ENDPOINTS.profitabilityV2.configHistoryClear();
  }
  if (kind === 'vehicle-attributes') {
    return API_ENDPOINTS.fleetIntelligence.vehicles.attributesHistoryClear();
  }
  return API_ENDPOINTS.utilisationV2.configHistoryClear();
}

export async function fetchConfigAssumptionsHistory(
  kind: ConfigHistoryKind,
): Promise<ConfigHistoryDocument> {
  const response = await authenticatedFetch(historyUrl(kind), { cache: 'no-store' });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Config history load failed: ${response.status} ${text.slice(0, 200)}`);
  }
  return (await response.json()) as ConfigHistoryDocument;
}

/** Admin-only: archive then empty the operator-visible history for this kind. */
export async function clearConfigAssumptionsHistory(
  kind: ConfigHistoryKind,
): Promise<{ success: boolean; cleared: number; archived: boolean }> {
  const response = await authenticatedFetch(historyClearUrl(kind), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: '{}',
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Clear config history failed: ${response.status} ${text.slice(0, 200)}`);
  }
  return (await response.json()) as { success: boolean; cleared: number; archived: boolean };
}
